import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import axios from '@/utils/axiosConfig';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  DollarSign, Plus, TrendingUp, TrendingDown, Wallet, Calendar,
  Home, Zap, Wifi, Users, Fuel, FileText, ShoppingCart, CreditCard,
  Filter, Download, PieChart, BarChart3, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useIsMounted } from '@/hooks/useSafeState';
import TreasuryCharts from '@/components/TreasuryCharts';

const Tresorerie = () => {
  const { user, token } = useAuth();
  const isMountedRef = useIsMounted();
  const [bankAccount, setBankAccount] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list', 'stats', or 'charts'

  const [filters, setFilters] = useState({
    dateFrom: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
    dateTo: format(endOfMonth(new Date()), 'yyyy-MM-dd'),
    type: 'all', // 'all', 'income', 'expense'
    category: 'all'
  });

  const [formData, setFormData] = useState({
    transaction_type: 'income',
    category: 'revenue_client',
    amount: 0,
    description: '',
    date: new Date().toISOString().split('T')[0]
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  // Catégories de transactions
  const categories = {
    // Revenus (entrées)
    income: [
      { value: 'revenue_client', label: 'Paiement Client', icon: Users },
      { value: 'revenue_service', label: 'Revenu Service', icon: FileText },
      { value: 'revenue_autre', label: 'Autre Revenu', icon: DollarSign }
    ],
    // Dépenses (sorties)
    expense: [
      { value: 'salaire', label: 'Salaire', icon: Users },
      { value: 'loyer', label: 'Loyer', icon: Home },
      { value: 'electricite', label: 'Électricité', icon: Zap },
      { value: 'internet', label: 'Internet', icon: Wifi },
      { value: 'gasoil', label: 'Gasoil/Carburant', icon: Fuel },
      { value: 'fourniture', label: 'Fournitures', icon: ShoppingCart },
      { value: 'autre_depense', label: 'Autre Dépense', icon: CreditCard }
    ]
  };

  useEffect(() => {
    const abortController = new AbortController();

    const loadData = async () => {
      try {
        const [bankRes, transRes] = await Promise.all([
          axios.get(`${API}/company-bank-account`, {
            headers: { Authorization: `Bearer ${token}` },
            signal: abortController.signal
          }),
          axios.get(`${API}/bank-transactions?limit=500`, {
            headers: { Authorization: `Bearer ${token}` },
            signal: abortController.signal
          })
        ]);

        if (isMountedRef.current) {
          setBankAccount(bankRes.data);
          setTransactions(transRes.data);
        }
      } catch (error) {
        if (error.name !== 'CanceledError' && error.name !== 'AbortError' && isMountedRef.current) {
          toast.error('Erreur lors du chargement');
          console.error(error);
        }
      } finally {
        if (isMountedRef.current) setLoading(false);
      }
    };

    loadData();

    return () => {
      abortController.abort();
    };
  }, [token]);

  // Filter transactions whenever filters or transactions change
  useEffect(() => {
    if (transactions.length === 0) {
      setFilteredTransactions([]);
      return;
    }

    let filtered = [...transactions];

    // Filter by date range
    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      filtered = filtered.filter(t => new Date(t.created_at) >= fromDate);
    }
    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59);
      filtered = filtered.filter(t => new Date(t.created_at) <= toDate);
    }

    // Filter by type
    if (filters.type !== 'all') {
      if (filters.type === 'income') {
        filtered = filtered.filter(t => t.amount > 0);
      } else if (filters.type === 'expense') {
        filtered = filtered.filter(t => t.amount < 0);
      }
    }

    // Filter by category
    if (filters.category !== 'all') {
      filtered = filtered.filter(t => t.transaction_type === filters.category);
    }

    setFilteredTransactions(filtered);
  }, [transactions, filters]);

  const fetchData = async (signal = null) => {
    try {
      const [bankRes, transRes] = await Promise.all([
        axios.get(`${API}/company-bank-account`, {
          headers: { Authorization: `Bearer ${token}` },
          ...(signal && { signal })
        }),
        axios.get(`${API}/bank-transactions?limit=500`, {
          headers: { Authorization: `Bearer ${token}` },
          ...(signal && { signal })
        })
      ]);

      if (isMountedRef.current) {
        setBankAccount(bankRes.data);
        setTransactions(transRes.data);
      }
    } catch (error) {
      if (error.name !== 'CanceledError' && error.name !== 'AbortError') {
        console.error('Error fetching data:', error);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const abortController = new AbortController();

    if (!formData.amount || formData.amount <= 0) {
      toast.error('Veuillez entrer un montant valide');
      return;
    }

    if (!formData.description.trim()) {
      toast.error('Veuillez entrer une description');
      return;
    }

    try {
      // Calculer le montant avec le bon signe
      const adjustmentAmount = formData.transaction_type === 'income'
        ? Math.abs(formData.amount)
        : -Math.abs(formData.amount);

      const params = new URLSearchParams({
        amount: adjustmentAmount.toString(),
        description: `${getCategoryLabel(formData.category)} - ${formData.description}`,
        transaction_type: formData.category
      });

      await axios.put(
        `${API}/company-bank-account/adjust?${params}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          signal: abortController.signal
        }
      );

      toast.success(formData.transaction_type === 'income' 
        ? '✅ Revenu ajouté avec succès' 
        : '✅ Dépense enregistrée avec succès'
      );

      setDialogOpen(false);
      resetForm();
      await fetchData(abortController.signal);
    } catch (error) {
      if (error.name !== 'CanceledError') {
        const errorMsg = typeof error.response?.data?.detail === 'string'
          ? error.response.data.detail
          : error.message || 'Erreur lors de l\'enregistrement';
        toast.error(errorMsg);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      transaction_type: 'income',
      category: 'revenue_client',
      amount: 0,
      description: '',
      date: new Date().toISOString().split('T')[0]
    });
  };

  const getCategoryLabel = (category) => {
    const allCategories = [...categories.income, ...categories.expense];
    const cat = allCategories.find(c => c.value === category);
    return cat ? cat.label : category;
  };

  const getCategoryIcon = (category) => {
    const allCategories = [...categories.income, ...categories.expense];
    const cat = allCategories.find(c => c.value === category);
    return cat ? cat.icon : FileText;
  };

  const getTransactionTypeInfo = (type) => {
    if (type === 'income' || type.startsWith('revenue')) {
      return { color: 'text-green-600', bg: 'bg-green-100', label: 'Revenu', icon: TrendingUp };
    } else if (type === 'payment_salary') {
      return { color: 'text-blue-600', bg: 'bg-blue-100', label: 'Salaire', icon: Users };
    } else {
      return { color: 'text-red-600', bg: 'bg-red-100', label: 'Dépense', icon: TrendingDown };
    }
  };

  // Calcul des statistiques globales
  const stats = filteredTransactions.reduce((acc, trans) => {
    if (trans.amount > 0) {
      acc.totalRevenue += trans.amount;
    } else {
      acc.totalExpense += Math.abs(trans.amount);
    }
    return acc;
  }, { totalRevenue: 0, totalExpense: 0 });

  // Calcul des statistiques par catégorie
  const categoryStats = filteredTransactions.reduce((acc, trans) => {
    const category = trans.transaction_type || 'autre';
    if (!acc[category]) {
      acc[category] = { total: 0, count: 0 };
    }
    acc[category].total += Math.abs(trans.amount);
    acc[category].count += 1;
    return acc;
  }, {});

  // Trier les catégories par montant total
  const topCategories = Object.entries(categoryStats)
    .map(([cat, data]) => ({
      category: cat,
      ...data,
      label: getCategoryLabel(cat)
    }))
    .sort((a, b) => b.total - a.total)
    .slice(0, 5);

  const handleExportTransactions = () => {
    if (filteredTransactions.length === 0) {
      toast.error('Aucune transaction à exporter');
      return;
    }

    // Create CSV content
    const headers = ['Date', 'Type', 'Catégorie', 'Description', 'Montant', 'Solde Après'];
    const rows = filteredTransactions.map(trans => [
      format(new Date(trans.created_at), 'dd/MM/yyyy HH:mm', { locale: fr }),
      trans.amount > 0 ? 'Revenu' : 'Dépense',
      getCategoryLabel(trans.transaction_type),
      trans.description,
      trans.amount.toFixed(2),
      trans.balance_after.toFixed(2)
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `transactions_${format(new Date(), 'yyyy-MM-dd')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('✅ Transactions exportées');
  };

  const handleQuickFilter = (period) => {
    const now = new Date();
    switch (period) {
      case 'this_month':
        setFilters({
          ...filters,
          dateFrom: format(startOfMonth(now), 'yyyy-MM-dd'),
          dateTo: format(endOfMonth(now), 'yyyy-MM-dd')
        });
        break;
      case 'last_month':
        const lastMonth = subMonths(now, 1);
        setFilters({
          ...filters,
          dateFrom: format(startOfMonth(lastMonth), 'yyyy-MM-dd'),
          dateTo: format(endOfMonth(lastMonth), 'yyyy-MM-dd')
        });
        break;
      case 'all':
        setFilters({
          ...filters,
          dateFrom: '',
          dateTo: ''
        });
        break;
      default:
        break;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Chargement...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Wallet className="h-8 w-8 text-blue-600" />
              Gestion de Trésorerie
            </h1>
            <p className="text-gray-600 mt-1">Gérez tous les revenus et dépenses de l'entreprise</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleExportTransactions}
              className="border-blue-600 text-blue-600 hover:bg-blue-50"
            >
              <Download className="h-4 w-4 mr-2" />
              Exporter
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                if (viewMode === 'list') setViewMode('stats');
                else if (viewMode === 'stats') setViewMode('charts');
                else setViewMode('list');
              }}
              className="border-purple-600 text-purple-600 hover:bg-purple-50"
            >
              {viewMode === 'list' && <PieChart className="h-4 w-4 mr-2" />}
              {viewMode === 'stats' && <BarChart3 className="h-4 w-4 mr-2" />}
              {viewMode === 'charts' && <Eye className="h-4 w-4 mr-2" />}
              {viewMode === 'list' && 'Statistiques'}
              {viewMode === 'stats' && 'Graphiques'}
              {viewMode === 'charts' && 'Liste'}
            </Button>
            <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="h-4 w-4 mr-2" />
              Nouvelle Transaction
            </Button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* Solde Actuel */}
          <Card className="border-2 border-blue-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Solde Actuel</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {bankAccount?.balance.toFixed(2)} $
                  </p>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <Wallet className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Total Revenus */}
          <Card className="border-2 border-green-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Revenus</p>
                  <p className="text-2xl font-bold text-green-600">
                    +{stats.totalRevenue.toFixed(2)} $
                  </p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <TrendingUp className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Total Dépenses */}
          <Card className="border-2 border-red-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Dépenses</p>
                  <p className="text-2xl font-bold text-red-600">
                    -{stats.totalExpense.toFixed(2)} $
                  </p>
                </div>
                <div className="p-3 bg-red-100 rounded-full">
                  <TrendingDown className="h-6 w-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Bilan */}
          <Card className="border-2 border-gray-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Bilan</p>
                  <p className={`text-2xl font-bold ${stats.totalRevenue - stats.totalExpense >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {(stats.totalRevenue - stats.totalExpense).toFixed(2)} $
                  </p>
                </div>
                <div className="p-3 bg-gray-100 rounded-full">
                  <DollarSign className="h-6 w-6 text-gray-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters Section */}
        <Card className="bg-gradient-to-r from-blue-50 to-purple-50">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Filter className="h-5 w-5 text-blue-600" />
                Filtres et Période
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
              >
                {showFilters ? 'Masquer' : 'Afficher'}
              </Button>
            </div>
          </CardHeader>
          {showFilters && (
            <CardContent className="space-y-4">
              {/* Quick Filters */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Période rapide</Label>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleQuickFilter('this_month')}
                    className="text-xs"
                  >
                    Ce mois
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleQuickFilter('last_month')}
                    className="text-xs"
                  >
                    Mois dernier
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleQuickFilter('all')}
                    className="text-xs"
                  >
                    Tout
                  </Button>
                </div>
              </div>

              {/* Custom Date Range */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <Label>Date début</Label>
                  <Input
                    type="date"
                    value={filters.dateFrom}
                    onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Date fin</Label>
                  <Input
                    type="date"
                    value={filters.dateTo}
                    onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Type</Label>
                  <Select
                    value={filters.type}
                    onValueChange={(value) => setFilters({ ...filters, type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous</SelectItem>
                      <SelectItem value="income">Revenus uniquement</SelectItem>
                      <SelectItem value="expense">Dépenses uniquement</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Catégorie</Label>
                  <Select
                    value={filters.category}
                    onValueChange={(value) => setFilters({ ...filters, category: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes</SelectItem>
                      <optgroup label="Revenus">
                        {categories.income.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>
                            {cat.label}
                          </SelectItem>
                        ))}
                      </optgroup>
                      <optgroup label="Dépenses">
                        {categories.expense.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>
                            {cat.label}
                          </SelectItem>
                        ))}
                      </optgroup>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Results Summary */}
              <div className="pt-3 border-t">
                <p className="text-sm text-gray-600">
                  <strong>{filteredTransactions.length}</strong> transaction(s) trouvée(s) sur {transactions.length} au total
                </p>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Statistics by Category (if in stats view) */}
        {viewMode === 'stats' && (
          <Card className="bg-gradient-to-br from-purple-50 to-blue-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-6 w-6 text-purple-600" />
                Top 5 Catégories de Dépenses
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {topCategories.map((cat, index) => {
                  const percentage = (cat.total / stats.totalExpense) * 100 || 0;
                  return (
                    <div key={cat.category} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl font-bold text-purple-600">#{index + 1}</span>
                          <span className="font-medium">{cat.label}</span>
                          <Badge variant="outline">{cat.count} transaction(s)</Badge>
                        </div>
                        <span className="text-lg font-bold text-gray-900">
                          {cat.total.toFixed(2)} $
                        </span>
                      </div>
                      <div className="relative w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="absolute top-0 left-0 h-full bg-gradient-to-r from-purple-600 to-blue-600 rounded-full transition-all duration-500"
                          style={{ width: `${Math.min(percentage, 100)}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-600">
                        {percentage.toFixed(1)}% du total des dépenses
                      </p>
                    </div>
                  );
                })}
                {topCategories.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <PieChart className="h-12 w-12 mx-auto mb-4 opacity-30" />
                    <p>Aucune donnée de catégorie disponible</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Charts View */}
        {viewMode === 'charts' && (
          <TreasuryCharts
            transactions={filteredTransactions}
            categoryStats={categoryStats}
            stats={stats}
          />
        )}

        {/* Transactions History */}
        <Card>
          <CardHeader>
            <CardTitle>Historique des Transactions ({filteredTransactions.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {filteredTransactions.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Wallet className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>Aucune transaction enregistrée</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredTransactions.map((trans) => {
                  const typeInfo = getTransactionTypeInfo(trans.transaction_type);
                  const Icon = getCategoryIcon(trans.transaction_type);
                  
                  return (
                    <div
                      key={trans.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`p-3 ${typeInfo.bg} rounded-lg`}>
                          <Icon className={`h-6 w-6 ${typeInfo.color}`} />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{trans.description}</p>
                          <div className="flex items-center gap-3 mt-1 text-sm text-gray-600">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {format(new Date(trans.created_at), 'dd/MM/yyyy HH:mm', { locale: fr })}
                            </span>
                            <Badge className={typeInfo.bg + ' ' + typeInfo.color}>
                              {typeInfo.label}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`text-xl font-bold ${trans.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {trans.amount >= 0 ? '+' : ''}{trans.amount.toFixed(2)} $
                        </p>
                        <p className="text-sm text-gray-500">
                          Solde: {trans.balance_after.toFixed(2)} $
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add Transaction Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Nouvelle Transaction</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Type de transaction */}
              <div>
                <Label>Type de transaction *</Label>
                <Select
                  value={formData.transaction_type}
                  onValueChange={(value) => {
                    setFormData({
                      ...formData,
                      transaction_type: value,
                      category: value === 'income' ? 'revenue_client' : 'loyer'
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="income">
                      <div className="flex items-center gap-2">
                        <TrendingUp className="h-4 w-4 text-green-600" />
                        Revenu (Entrée d'argent)
                      </div>
                    </SelectItem>
                    <SelectItem value="expense">
                      <div className="flex items-center gap-2">
                        <TrendingDown className="h-4 w-4 text-red-600" />
                        Dépense (Sortie d'argent)
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Catégorie */}
              <div>
                <Label>Catégorie *</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories[formData.transaction_type].map((cat) => {
                      const Icon = cat.icon;
                      return (
                        <SelectItem key={cat.value} value={cat.value}>
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4" />
                            {cat.label}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              {/* Montant */}
              <div>
                <Label>Montant (CAD) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                  required
                  min="0.01"
                  placeholder="0.00"
                />
              </div>

              {/* Description */}
              <div>
                <Label>Description *</Label>
                <Input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  placeholder="Ex: Paiement client XYZ, Facture électricité novembre..."
                />
              </div>

              {/* Date */}
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                />
              </div>

              {/* Preview */}
              <Card className={`${formData.transaction_type === 'income' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">
                      {formData.transaction_type === 'income' ? 'Nouveau solde :' : 'Nouveau solde :'}
                    </span>
                    <span className={`text-lg font-bold ${formData.transaction_type === 'income' ? 'text-green-600' : 'text-blue-600'}`}>
                      {formData.transaction_type === 'income'
                        ? (bankAccount?.balance + formData.amount).toFixed(2)
                        : (bankAccount?.balance - formData.amount).toFixed(2)
                      } $
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setDialogOpen(false); resetForm(); }}
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  className={formData.transaction_type === 'income' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
                >
                  {formData.transaction_type === 'income' ? 'Ajouter Revenu' : 'Enregistrer Dépense'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Tresorerie;
